// ─────────────────────────────────────────────────────────────
// sentry.utils.ts
//
// Thin capture helpers. `captureApiError` is called from the store's
// rtkQueryErrorLogger for failed API calls; the generic wrappers are for
// ad-hoc try/catch reporting elsewhere.
// ─────────────────────────────────────────────────────────────

import * as Sentry from "@sentry/react";
import { ApiErrorContext } from "./sentry.types";

/**
 * Report a failed API call to Sentry.
 *
 * Only real breakage is reported: server errors (>= 500) and network / fetch
 * failures (non-numeric or 0 status). Client errors (4xx — validation, auth,
 * not-found) are expected and handled by the app, so they are skipped to keep
 * the issue stream actionable.
 */
export const captureApiError = (ctx: ApiErrorContext): void => {
  const { endpoint, status, detail, userId, orgId, url } = ctx;

  const numericStatus = typeof status === "number" ? status : Number(status);
  const isNetworkError = Number.isNaN(numericStatus) || numericStatus === 0;
  const isServerError = numericStatus >= 500;

  if (!isServerError && !isNetworkError) return;

  Sentry.captureException(
    new Error(`API ${status || "ERR"} ${endpoint}: ${detail}`),
    {
      tags: {
        type: "api_error",
        endpoint,
        status_code: String(status),
        org_code: orgId,
      },
      extra: { url, user_id: userId },
    }
  );
};

/** Ad-hoc exception reporting for try/catch blocks that would otherwise swallow. */
export const captureError = (
  error: unknown,
  context?: Record<string, unknown>
): void => {
  Sentry.captureException(error, context ? { extra: context } : undefined);
};

/** Ad-hoc message reporting. */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = "info"
): void => {
  Sentry.captureMessage(message, level);
};
